import React, { useState, useEffect } from 'react';
import { X, Settings, Cookie, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackCookieConsent, trackCookieBannerInteraction } from '@/utils/analytics';
import { supabase } from '@/integrations/supabase/client';
// Cookie preference categories
interface CookiePreferences {
  necessary: boolean;      // Always true
  functionality: boolean;  // Funzionalità
  experience: boolean;     // Esperienza
  measurement: boolean;    // Misurazione (Analytics)
  marketing: boolean;      // Marketing
}

// IAB TCF 2.2 Purposes
interface TCFPurposes {
  purpose1: boolean;  // Store and/or access information on a device
  purpose2: boolean;  // Select basic ads
  purpose3: boolean;  // Create a personalized ads profile
  purpose4: boolean;  // Select personalized ads
  purpose5: boolean;  // Create a personalized content profile
  purpose6: boolean;  // Select personalized content
  purpose7: boolean;  // Measure ad performance
  purpose8: boolean;  // Measure content performance
  purpose9: boolean;  // Apply market research to generate audience insights
  purpose10: boolean; // Develop and improve products
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  functionality: false,
  experience: false,
  measurement: false,
  marketing: false,
};

const defaultTCFPurposes: TCFPurposes = {
  purpose1: false,
  purpose2: false,
  purpose3: false,
  purpose4: false,
  purpose5: false,
  purpose6: false,
  purpose7: false,
  purpose8: false,
  purpose9: false,
  purpose10: false,
};

// Generate TC String (simplified version for self-managed CMP)
const generateTCString = (purposes: TCFPurposes): string => {
  const timestamp = Math.floor(Date.now() / 100);
  const purposeConsents = Object.values(purposes).map(v => v ? '1' : '0').join('');
  // This is a simplified TC string - in production, use a proper TC string encoder
  return `CP${timestamp}${purposeConsents}`;
};

// Update IAB TCF API with consent
const updateTCFConsent = (purposes: TCFPurposes, gdprApplies: boolean = true) => {
  if (typeof window !== 'undefined' && typeof (window as any).__tcfapi === 'function') {
    const tcString = generateTCString(purposes);
    const tcData = {
      tcString,
      tcfPolicyVersion: 2,
      cmpId: 0, // Self-managed
      cmpVersion: 1,
      gdprApplies,
      eventStatus: 'useractioncomplete',
      cmpStatus: 'loaded',
      listenerId: undefined,
      isServiceSpecific: true,
      useNonStandardTexts: false,
      publisherCC: 'IT',
      purposeOneTreatment: false,
      purpose: {
        consents: {
          1: purposes.purpose1,
          2: purposes.purpose2,
          3: purposes.purpose3,
          4: purposes.purpose4,
          5: purposes.purpose5,
          6: purposes.purpose6,
          7: purposes.purpose7,
          8: purposes.purpose8,
          9: purposes.purpose9,
          10: purposes.purpose10,
        },
        legitimateInterests: {}
      },
      vendor: {
        consents: {},
        legitimateInterests: {}
      },
      specialFeatureOptins: {},
      publisher: {
        consents: {},
        legitimateInterests: {},
        customPurpose: {
          consents: {},
          legitimateInterests: {}
        },
        restrictions: {}
      }
    };

    // Store for later retrieval
    (window as any).__tcfapiTCData = tcData;

    // Process any queued calls
    const queue = (window as any).__tcfapiQueue || [];
    queue.forEach((args: any[]) => {
      const [command, version, callback] = args;
      if (command === 'getTCData') {
        callback(tcData, true);
      } else if (command === 'addEventListener') {
        callback(tcData, true);
      }
    });
    (window as any).__tcfapiQueue = [];

    // Replace stub with full implementation
    (window as any).__tcfapi = (command: string, version: number, callback: Function, parameter?: any) => {
      if (command === 'ping') {
        callback({
          gdprApplies,
          cmpLoaded: true,
          cmpStatus: 'loaded',
          displayStatus: 'hidden',
          apiVersion: '2.2',
          cmpVersion: 1,
          cmpId: 0,
          gvlVersion: 0,
          tcfPolicyVersion: 2
        }, true);
      } else if (command === 'getTCData') {
        callback(tcData, true);
      } else if (command === 'addEventListener') {
        callback(tcData, true);
        return 1; // listenerId
      } else if (command === 'removeEventListener') {
        callback(true);
      }
    };

    // Store TC string in localStorage
    localStorage.setItem('tcString', tcString);
    localStorage.setItem('tcfPurposes', JSON.stringify(purposes));
  }
};

// Map cookie preferences to TCF purposes
const mapPreferencesToTCF = (prefs: CookiePreferences): TCFPurposes => {
  return {
    purpose1: prefs.necessary, // Store/access device info
    purpose2: prefs.marketing, // Select basic ads
    purpose3: prefs.marketing, // Create personalized ads profile
    purpose4: prefs.marketing, // Select personalized ads
    purpose5: prefs.experience, // Create personalized content profile
    purpose6: prefs.experience, // Select personalized content
    purpose7: prefs.measurement, // Measure ad performance
    purpose8: prefs.measurement, // Measure content performance
    purpose9: prefs.measurement, // Market research
    purpose10: prefs.functionality, // Develop and improve products
  };
};

// Floating widget component for reopening preferences
export const CookiePreferencesWidget = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Show widget only if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      setIsVisible(true);
      
      // Restore TCF consent on page load
      const savedTCF = localStorage.getItem('tcfPurposes');
      if (savedTCF) {
        try {
          const purposes = JSON.parse(savedTCF);
          updateTCFConsent(purposes, true);
        } catch (e) {
          console.error('Error restoring TCF consent');
        }
      }
    }
  }, []);

  const handleOpenBanner = () => {
    setShowBanner(true);
    trackCookieBannerInteraction('widget_clicked');
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating cookie button */}
      <button
        onClick={handleOpenBanner}
        className="fixed bottom-4 left-4 z-40 bg-primary text-foreground p-3 rounded-full shadow-lg hover:bg-primary/90 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary/50"
        aria-label="Gestisci preferenze cookie"
        title="Gestisci preferenze cookie"
      >
        <Cookie className="w-5 h-5" />
      </button>

      {/* Show banner when clicked */}
      {showBanner && (
        <CookieConsentBanner 
          onClose={() => setShowBanner(false)} 
          forceShow={true}
        />
      )}
    </>
  );
};

interface CookieConsentBannerProps {
  onClose?: () => void;
  forceShow?: boolean;
}

// Category description component with expandable details
const CategoryItem = ({ 
  title, 
  description, 
  details,
  checked, 
  onChange, 
  disabled = false,
  alwaysActive = false,
  tcfPurposes
}: { 
  title: string;
  description: string;
  details: string;
  checked: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  alwaysActive?: boolean;
  tcfPurposes?: string;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`border rounded-lg p-4 ${alwaysActive ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900' : 'border-gray-200 dark:border-gray-700'}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
        <div className="flex items-center gap-2">
          {alwaysActive ? (
            <span className="text-xs bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
              Sempre attivi
            </span>
          ) : (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange?.(e.target.checked)}
                disabled={disabled}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 dark:peer-focus:ring-primary/40 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
            </label>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{description}</p>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-primary hover:underline flex items-center gap-1"
      >
        {expanded ? 'Nascondi descrizione' : 'Mostra descrizione'}
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {expanded && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 p-3 rounded space-y-2">
          <p>{details}</p>
          {tcfPurposes && (
            <p className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
              <Shield className="w-3 h-3" />
              <span>IAB TCF 2.2: {tcfPurposes}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const CookieConsentBanner = ({ onClose, forceShow = false }: CookieConsentBannerProps) => {
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    // Load existing preferences if any
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences(prev => ({ ...prev, ...parsed, necessary: true }));
      } catch (e) {
        console.error('Error parsing cookie preferences');
      }
    }
  }, []);

  const updateGtagConsent = (prefs: CookiePreferences) => {
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('consent', 'update', {
        'analytics_storage': prefs.measurement ? 'granted' : 'denied',
        'ad_storage': prefs.marketing ? 'granted' : 'denied',
        'ad_user_data': prefs.marketing ? 'granted' : 'denied',
        'ad_personalization': prefs.marketing ? 'granted' : 'denied',
        'functionality_storage': prefs.functionality ? 'granted' : 'denied',
        'personalization_storage': prefs.experience ? 'granted' : 'denied',
      });
    }
  };

  const saveConsent = async (prefs: CookiePreferences, consentType: 'accepted' | 'rejected' | 'custom') => {
    const existingConsent = localStorage.getItem('cookieConsent');
    const isUpdate = !!existingConsent;
    
    // Session ID removed for privacy - no longer tracked
    
    localStorage.setItem('cookieConsent', consentType);
    localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentTimestamp', new Date().toISOString());
    
    // Update Google Consent Mode
    updateGtagConsent(prefs);
    
    // Update IAB TCF 2.2
    const tcfPurposes = mapPreferencesToTCF(prefs);
    updateTCFConsent(tcfPurposes, true);
    
    // Track consent for analytics and GDPR audit
    trackCookieConsent(consentType, prefs, isUpdate);
    
    // Log to backend for GDPR audit trail - PRIVACY FIRST
    // Only send: consent_type, preferences, is_update
    // NO: session_id, IP, user agent, country
    supabase.functions.invoke('cookie-consent-audit', {
      body: {
        action: 'log',
        consent_type: consentType,
        preferences: prefs,
        is_update: isUpdate,
        // session_id removed for privacy
      },
    }).catch(err => console.error('Failed to log consent:', err));
  };

  const handleAcceptAll = () => {
    const newPreferences: CookiePreferences = { 
      necessary: true, 
      functionality: true, 
      experience: true, 
      measurement: true, 
      marketing: true 
    };
    saveConsent(newPreferences, 'accepted');
    onClose?.();
  };

  const handleRejectAll = () => {
    const newPreferences: CookiePreferences = { 
      necessary: true, 
      functionality: false, 
      experience: false, 
      measurement: false, 
      marketing: false 
    };
    saveConsent(newPreferences, 'rejected');
    onClose?.();
  };

  const handleSavePreferences = () => {
    saveConsent(preferences, 'custom');
    onClose?.();
  };

  const handleShowPreferences = () => {
    setShowPreferences(true);
    trackCookieBannerInteraction('preferences_opened');
  };

  const handleBackToMain = () => {
    setShowPreferences(false);
    trackCookieBannerInteraction('preferences_closed');
  };

  const handleClose = () => {
    // If it's forced show (from widget), just close
    if (forceShow) {
      onClose?.();
    }
    // Otherwise don't close without making a choice (GDPR requirement)
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full p-6 relative animate-in slide-in-from-bottom duration-300">
        {forceShow && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className={forceShow ? "pr-8" : ""}>
          {!showPreferences ? (
            <>
              <h2 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">
                🍪 Le tue preferenze relative alla privacy
              </h2>
              
              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                Questo pannello ti permette di esprimere alcune preferenze relative al trattamento delle tue informazioni personali.
                Puoi rivedere e modificare le tue scelte in qualsiasi momento accedendo al presente pannello tramite l'apposito link.
              </p>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Per rifiutare il tuo consenso alle attività di trattamento descritte di seguito, disattiva i singoli comandi o utilizza il pulsante "Rifiuta tutto" e conferma di voler salvare le scelte effettuate.
              </p>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Leggi la nostra{' '}
                <Link 
                  to="/cookie-policy" 
                  className="text-primary hover:underline font-medium"
                >
                  Cookie Policy
                </Link>
                {' '}e{' '}
                <Link 
                  to="/privacy-policy" 
                  className="text-primary hover:underline font-medium"
                >
                  Privacy Policy
                </Link>
                {' '}per maggiori dettagli.
              </p>

              {/* Vertical stacked buttons - GDPR requirement with equal prominence */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="w-full bg-primary text-foreground px-6 py-4 rounded-lg text-base font-bold hover:bg-primary/90 transition-colors shadow-lg"
                >
                  Accetta tutto
                </button>
                <button
                  onClick={handleRejectAll}
                  className="w-full bg-gray-600 text-white px-6 py-4 rounded-lg text-base font-bold hover:bg-gray-700 transition-colors shadow-lg"
                >
                  Rifiuta tutto
                </button>
                <button
                  onClick={handleShowPreferences}
                  className="w-full border-2 border-gray-300 dark:border-gray-600 bg-background text-foreground px-6 py-4 rounded-lg text-base font-bold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Personalizza preferenze
                </button>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-500 mt-4 text-center">
                Puoi modificare le tue scelte in qualsiasi momento cliccando sull'icona 🍪 in basso a sinistra.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                ⚙️ Le tue preferenze relative al consenso per le tecnologie di tracciamento
              </h2>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Le opzioni disponibili in questa sezione ti permettono di personalizzare le preferenze relative al consenso per qualsiasi tecnologia di tracciamento utilizzata per le finalità descritte di seguito.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-6">
                Tieni presente che il rifiuto del consenso per una finalità particolare può rendere le relative funzioni non disponibili.
              </p>

              {/* Equal prominence buttons at top */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 bg-gray-600 text-white px-4 py-3 rounded-lg text-sm font-bold hover:bg-gray-700 transition-colors shadow-lg"
                >
                  Rifiuta tutto
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 bg-primary text-foreground px-4 py-3 rounded-lg text-sm font-bold hover:bg-primary/90 transition-colors shadow-lg"
                >
                  Accetta tutto
                </button>
              </div>

              <div className="space-y-3 mb-6 max-h-[40vh] overflow-y-auto pr-2">
                {/* Necessari */}
                <CategoryItem
                  title="Necessari"
                  description="Questi strumenti di tracciamento sono strettamente necessari per garantire il funzionamento e la fornitura del servizio."
                  details="Questi cookie sono essenziali per il funzionamento del sito web e non possono essere disattivati. Includono cookie per la gestione delle preferenze cookie, la protezione anti-spam (Cloudflare Turnstile) e le funzionalità di sicurezza di base. Senza questi cookie, il sito web non può funzionare correttamente."
                  checked={preferences.necessary}
                  alwaysActive={true}
                  tcfPurposes="Finalità 1 - Archiviare e/o accedere a informazioni su un dispositivo"
                />

                {/* Funzionalità */}
                <CategoryItem
                  title="Funzionalità"
                  description="Questi strumenti di tracciamento consentono al servizio di offrire determinate funzionalità."
                  details="Questi cookie permettono al sito di ricordare le scelte che hai fatto (come il tuo nome utente, la lingua o la regione in cui ti trovi) e di fornire funzionalità personalizzate e avanzate. Possono essere impostati da noi o da fornitori terzi i cui servizi abbiamo aggiunto alle nostre pagine."
                  checked={preferences.functionality}
                  onChange={(checked) => setPreferences({ ...preferences, functionality: checked })}
                  tcfPurposes="Finalità 10 - Sviluppare e migliorare i prodotti"
                />

                {/* Esperienza */}
                <CategoryItem
                  title="Esperienza"
                  description="Questi strumenti di tracciamento ci permettono di migliorare la qualità della tua esperienza utente."
                  details="Questi cookie ci permettono di contare le visite e le fonti di traffico per misurare e migliorare le prestazioni del nostro sito. Ci aiutano a sapere quali pagine sono più o meno popolari e a vedere come i visitatori navigano nel sito. Se non accetti questi cookie, non sapremo quando hai visitato il nostro sito."
                  checked={preferences.experience}
                  onChange={(checked) => setPreferences({ ...preferences, experience: checked })}
                  tcfPurposes="Finalità 5, 6 - Creare e selezionare contenuti personalizzati"
                />

                {/* Misurazione */}
                <CategoryItem
                  title="Misurazione"
                  description="Questi strumenti di tracciamento ci permettono di misurare il traffico e analizzare il tuo comportamento."
                  details="Questi cookie vengono utilizzati per raccogliere informazioni su come i visitatori utilizzano il nostro sito. Usiamo queste informazioni per compilare report e per aiutarci a migliorare il sito. I cookie raccolgono informazioni in forma anonima, incluso il numero di visitatori del sito, da dove i visitatori sono arrivati al sito e le pagine che hanno visitato. Provider: Google Analytics 4."
                  checked={preferences.measurement}
                  onChange={(checked) => setPreferences({ ...preferences, measurement: checked })}
                  tcfPurposes="Finalità 7, 8, 9 - Misurare le prestazioni e generare audience insights"
                />

                {/* Marketing */}
                <CategoryItem
                  title="Marketing"
                  description="Questi strumenti di tracciamento ci permettono di fornirti contenuti commerciali personalizzati."
                  details="Questi cookie possono essere impostati attraverso il nostro sito dai nostri partner pubblicitari. Possono essere utilizzati da queste aziende per costruire un profilo dei tuoi interessi e mostrarti annunci pertinenti su altri siti. Non memorizzano direttamente informazioni personali, ma sono basati sull'identificazione univoca del tuo browser e dispositivo internet. Attualmente utilizziamo: Amazon Associates per i prodotti consigliati."
                  checked={preferences.marketing}
                  onChange={(checked) => setPreferences({ ...preferences, marketing: checked })}
                  tcfPurposes="Finalità 2, 3, 4 - Selezionare annunci e creare profili pubblicitari personalizzati"
                />
              </div>

              {/* IAB TCF Badge */}
              <div className="flex items-center justify-center gap-2 mb-4 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-xs text-blue-700 dark:text-blue-300">
                  Conforme a IAB Transparency and Consent Framework (TCF) v2.2
                </span>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="w-full bg-primary text-foreground px-8 py-4 rounded-lg text-lg font-bold hover:bg-primary/90 transition-colors shadow-lg"
                >
                  💾 Salva Preferenze
                </button>
                <button
                  onClick={handleBackToMain}
                  className="w-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  ← Indietro
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Defer cookie banner well after LCP to avoid it becoming the LCP element
      const showBanner = () => {
        setIsVisible(true);
        trackCookieBannerInteraction('shown');
      };
      
      // Wait 6 seconds to ensure LCP has already been measured
      setTimeout(showBanner, 6000);
    }
  }, []);

  if (!isVisible) return null;

  return <CookieConsentBanner onClose={() => setIsVisible(false)} />;
};

export default CookieConsent;
